/**
 * Viewport-aware raised layer for automation menus.
 *
 * The panel portals to `document.body`, follows its invoking control during
 * scroll and resize, flips above when the lower viewport is too tight, and
 * shifts horizontally inside a small viewport gutter.
 *
 * @module components/admin/automation/AnchoredPopover
 */
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Portal } from '@headlessui/react';

/** Horizontal edge used to align a panel with its invoking control. */
export type PopoverAlignment = 'start' | 'center' | 'end';

/** Props accepted by {@link AnchoredPopover}. */
export interface AnchoredPopoverProps {
  /** Control whose viewport rectangle anchors the panel. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Ref used by outside-click and focus-management owners. */
  panelRef: React.RefObject<HTMLDivElement | null>;
  /** Horizontal alignment against the control. */
  align?: PopoverAlignment;
  /** Panel classes, including its intrinsic width. */
  className?: string;
  /** Panel contents. */
  children: React.ReactNode;
  /** Optional accessibility role. */
  role?: React.AriaRole;
  /** Optional test id. */
  testId?: string;
}

/** Measured placement rendered on the portalled panel. */
interface PanelPosition {
  left: number;
  top: number;
  maxHeight: number;
  placement: 'top' | 'bottom';
}

const VIEWPORT_GUTTER = 8;
const ANCHOR_GAP = 4;

/** Clamp a number between inclusive bounds. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Portalled panel positioned against a live control rectangle.
 *
 * @param props See {@link AnchoredPopoverProps}.
 */
export const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  anchorRef,
  panelRef,
  align = 'start',
  className,
  children,
  role,
  testId,
}) => {
  const [position, setPosition] = useState<PanelPosition | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const panelWidth = panelRect.width || panel.offsetWidth;
    const panelHeight = panelRect.height || panel.offsetHeight;
    const spaceBelow = window.innerHeight - anchorRect.bottom - ANCHOR_GAP - VIEWPORT_GUTTER;
    const spaceAbove = anchorRect.top - ANCHOR_GAP - VIEWPORT_GUTTER;
    const placement = panelHeight > spaceBelow && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const maxHeight = Math.max(VIEWPORT_GUTTER, placement === 'bottom' ? spaceBelow : spaceAbove);
    const top = placement === 'bottom'
      ? anchorRect.bottom + ANCHOR_GAP
      : Math.max(VIEWPORT_GUTTER, anchorRect.top - ANCHOR_GAP - Math.min(panelHeight, maxHeight));

    let desiredLeft = anchorRect.left;
    if (align === 'center') desiredLeft = anchorRect.left + (anchorRect.width - panelWidth) / 2;
    if (align === 'end') desiredLeft = anchorRect.right - panelWidth;
    const left = clamp(
      desiredLeft,
      VIEWPORT_GUTTER,
      window.innerWidth - panelWidth - VIEWPORT_GUTTER,
    );

    setPosition({ left, top, maxHeight, placement });
  }, [align, anchorRef, panelRef]);

  /** Portal children may mount after the owner's layout effect. Measure on attachment too. */
  const attachPanel = useCallback((element: HTMLDivElement | null) => {
    panelRef.current = element;
    if (element) updatePosition();
  }, [panelRef, updatePosition]);

  useLayoutEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updatePosition);
    if (anchorRef.current) resizeObserver?.observe(anchorRef.current);
    if (panelRef.current) resizeObserver?.observe(panelRef.current);

    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, panelRef, updatePosition]);

  return (
    <Portal>
      <div
        ref={attachPanel}
        role={role}
        data-testid={testId}
        data-placement={position?.placement}
        className={className}
        style={{
          position: 'fixed',
          left: position?.left ?? 0,
          top: position?.top ?? 0,
          maxHeight: position?.maxHeight,
          maxWidth: `calc(100vw - ${VIEWPORT_GUTTER * 2}px)`,
          visibility: position ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </Portal>
  );
};
